import { StrictMode, useContext, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Routes, Route, useLocation } from 'react-router'
import { ROUTES } from './config/routes'
import './resources/styles/main.css'
import { FormProvider } from './context/FormContext'
import { FormValidationProvider } from '@/context/FormValidationContext'
import { FormSaveForLaterProvider } from '@/hooks/useFormSaveForLater'
import { DealerProvider } from './context/DealerContext'
import { DealerContext } from './context/DealerContext'
import { initGTM } from './utils/gtm'
import { initSentry, Sentry } from './utils/sentry'
import { useGTMTracking } from './hooks/useGTMTracking'
import HomePage from './pages/HomePage'
import DealerPage from './pages/DealerPage'
import PersonalLoanLayout from './pages/personal/PersonalLoanLayout'
import PersonalPage from './pages/personal/PersonalPage'
import AboutYou from './pages/personal/form-steps/AboutYou'
import YourDetails from './pages/personal/form-steps/YourDetails'
import Income from './pages/personal/form-steps/Income'
import FinancialPosition from './pages/personal/form-steps/FinancialPosition'
import DocumentUpload from './pages/personal/form-steps/DocumentUpload'
import EndApplication from './pages/personal/form-steps/EndApplication'
import BusinessPage from './pages/business/BusinessPage'
import BusinessLoanLayout from './pages/business/BusinessLoanLayout'
import AboutYourBusiness from './pages/business/form-steps/AboutYourBusiness'
import AboutYouBusiness from './pages/business/form-steps/AboutYou'
import FinancialPositionBusiness from './pages/business/form-steps/FinancialPosition'
import ApplicationOptions from './pages/business/form-steps/ApplicationOptions'
import EndApplicationBusiness from './pages/business/form-steps/EndApplication'
import DealerSwitcher from './components/DealerSwitcher'
import { ProtectedFormRoute } from './components/ProtectedFormRoute'

// Redirect /Home/Dealer to ROUTES.DEALER.path, preserving query params
const dealerLegacyPath = '/home/dealer'
if (window.location.pathname.toLowerCase() === dealerLegacyPath) {
  const params = window.location.search
  window.location.replace(`${ROUTES.DEALER.path}${params}`)
}

// Component to notify parent on route change
function RouteChangeNotifier() {
  const location = useLocation()
  useEffect(() => {
    window.parent.postMessage('UDCviewChange', '*')
  }, [location])
  return null
}

// Component to handle GTM tracking inside router context
function GTMTracker() {
  useGTMTracking()
  return null
}

function SentryFallback () {
  return (
    <div className="p-8 text-center">
      <p>Something went wrong.</p>
      <button
        type="button"
        className="mt-4 underline"
        onClick={() => window.location.reload()}
      >
        Reload page
      </button>
    </div>
  )
}

const App = () => {
  const dealerContext = useContext(DealerContext)

  // Don't need to do anything special, it will just pick the default background of the application which we are happy with 
  if (!dealerContext || !dealerContext.isApplicationLoaded) {
    return null
  }

  return (
    <BrowserRouter>
      <RouteChangeNotifier />
      <GTMTracker />
      <FormProvider>
        <FormValidationProvider>
          <FormSaveForLaterProvider>
            <Routes>
              <Route path={ROUTES.HOME.path} element={<HomePage />} />
              <Route path={ROUTES.DEALER.path} element={<DealerPage />} />
              
              <Route path={ROUTES.PERSONAL.ROOT.path} element={<PersonalPage />} />
              <Route element={<PersonalLoanLayout />}>
                <Route path={ROUTES.PERSONAL.YOUR_DETAILS.path} element={
                  <ProtectedFormRoute path={ROUTES.PERSONAL.YOUR_DETAILS.path}>
                    <YourDetails />
                  </ProtectedFormRoute>
                } />
                <Route path={ROUTES.PERSONAL.ABOUT_YOU.path} element={
                  <ProtectedFormRoute path={ROUTES.PERSONAL.ABOUT_YOU.path}>
                    <AboutYou />
                  </ProtectedFormRoute>
                } />
                <Route path={ROUTES.PERSONAL.INCOME.path} element={
                  <ProtectedFormRoute path={ROUTES.PERSONAL.INCOME.path}>
                    <Income />
                  </ProtectedFormRoute>
                } />
                <Route path={ROUTES.PERSONAL.FINANCIAL_POSITION.path} element={
                  <ProtectedFormRoute path={ROUTES.PERSONAL.FINANCIAL_POSITION.path}>
                    <FinancialPosition />
                  </ProtectedFormRoute>
                } />
                <Route path={ROUTES.PERSONAL.DOC_UPLOAD.path} element={
                  <ProtectedFormRoute path={ROUTES.PERSONAL.DOC_UPLOAD.path}>
                    <DocumentUpload />
                  </ProtectedFormRoute>
                } />
                <Route path={ROUTES.PERSONAL.END_APPLICATION.path} element={
                  <ProtectedFormRoute path={ROUTES.PERSONAL.END_APPLICATION.path}>
                    <EndApplication />
                  </ProtectedFormRoute>
                } />
              </Route>

              <Route path={ROUTES.BUSINESS.ROOT.path} element={<BusinessPage />} />
              <Route element={<BusinessLoanLayout />}>
                <Route path={ROUTES.BUSINESS.ABOUT_YOUR_BUSINESS.path} element={
                  <ProtectedFormRoute path={ROUTES.BUSINESS.ABOUT_YOUR_BUSINESS.path}>
                    <AboutYourBusiness />
                  </ProtectedFormRoute>
                } />
                <Route path={ROUTES.BUSINESS.ABOUT_YOU.path} element={
                  <ProtectedFormRoute path={ROUTES.BUSINESS.ABOUT_YOU.path}>
                    <AboutYouBusiness />
                  </ProtectedFormRoute>
                } />
                <Route path={ROUTES.BUSINESS.FINANCIAL_POSITION.path} element={
                  <ProtectedFormRoute path={ROUTES.BUSINESS.FINANCIAL_POSITION.path}>
                    <FinancialPositionBusiness />
                  </ProtectedFormRoute>
                } />
                <Route path={ROUTES.BUSINESS.APPLICATION_OPTIONS.path} element={
                  <ProtectedFormRoute path={ROUTES.BUSINESS.APPLICATION_OPTIONS.path}>
                    <ApplicationOptions />
                  </ProtectedFormRoute>
                } />
                <Route path={ROUTES.BUSINESS.END_APPLICATION.path} element={
                  <ProtectedFormRoute path={ROUTES.BUSINESS.END_APPLICATION.path}>
                    <EndApplicationBusiness />
                  </ProtectedFormRoute>
                } />
              </Route>
            </Routes>
          </FormSaveForLaterProvider>
        </FormValidationProvider>
      </FormProvider>
    </BrowserRouter>
  )
}

initSentry()
initGTM()

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<SentryFallback />}>
      <DealerProvider>
        <DealerSwitcher />
        <App />
      </DealerProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>
)
